import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { addExceptionAPI, getWorkingHoursAPI, updateWorkingHoursAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { companyUrl } from '../../../utils/mhNav';
import styles from './WorkingHours.module.scss';

function toMin(t) {
  const [h, m] = String(t || '00:00').split(':').map(Number);
  return h * 60 + (m || 0);
}

export default function WorkingHours() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowErr, setRowErr] = useState({});
  const [exOpen, setExOpen] = useState(false);
  const [ex, setEx] = useState({ date: new Date(), description: '', type: 'Holiday' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWorkingHoursAPI();
      setCfg(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const validate = (days) => {
    const err = {};
    (days || []).forEach((d) => {
      if (d.enabled && toMin(d.end) <= toMin(d.start)) {
        err[d.day] = 'End must be after start';
      }
    });
    setRowErr(err);
    return !Object.keys(err).length;
  };

  const updateDay = (idx, patch) => {
    const weekdays = [...(cfg.weekdays || [])];
    weekdays[idx] = { ...weekdays[idx], ...patch };
    setCfg({ ...cfg, weekdays });
    validate(weekdays);
  };

  const copyMondayAll = () => {
    const weekdays = [...(cfg.weekdays || [])];
    const mon = weekdays.find((d) => d.day === 'mon');
    if (!mon) return;
    const next = weekdays.map((d) =>
      d.day === 'mon' ? d : { ...d, enabled: mon.enabled, start: mon.start, end: mon.end }
    );
    setCfg({ ...cfg, weekdays: next });
    validate(next);
  };

  const copyWeek = () => {
    copyMondayAll();
    toast.success('Copied week pattern');
  };

  const save = async () => {
    if (!validate(cfg.weekdays)) {
      toast.error('Fix validation errors');
      return;
    }
    try {
      await updateWorkingHoursAPI(cfg);
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const previewMonday = useMemo(() => {
    const mon = (cfg?.weekdays || []).find((d) => d.day === 'mon');
    if (!mon || !mon.enabled) return [];
    const start = toMin(mon.start);
    const end = toMin(mon.end);
    const slots = [];
    for (let t = start; t < end && slots.length < 8; t += 60) {
      const h = Math.floor(t / 60);
      const m = t % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
    return slots;
  }, [cfg]);

  if (loading && !cfg) {
    return (
      <div>
        <PageHeader title="Working hours" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Scheduling', path: companyUrl('scheduling') }, { label: 'Hours' }]} />
        <LoadingSkeleton rows={8} cols={2} />
      </div>
    );
  }

  if (error && !cfg) {
    return (
      <div>
        <PageHeader title="Working hours" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Scheduling', path: companyUrl('scheduling') }]} />
        <EmptyState icon={AlertTriangle} title="Error" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Working hours"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Scheduling', path: companyUrl('scheduling') },
          { label: 'Hours' },
        ]}
        secondaryActions={[
          { label: 'Copy week', onClick: copyWeek, variant: 'outline-secondary' },
          { label: 'Save', onClick: save, variant: 'primary' },
        ]}
      />

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Timezone: {cfg.timezone}</span>
            <Button size="sm" variant="outline-primary" onClick={copyMondayAll}>
              Copy to all days
            </Button>
          </div>
          <Table responsive size="sm">
            <thead>
              <tr>
                <th>Day</th>
                <th>Enabled</th>
                <th>Start</th>
                <th>End</th>
                <th>Timezone</th>
              </tr>
            </thead>
            <tbody>
              {(cfg.weekdays || []).map((d, idx) => (
                <tr key={d.day}>
                  <td>{d.label}</td>
                  <td>
                    <Form.Check checked={d.enabled} onChange={(e) => updateDay(idx, { enabled: e.target.checked })} />
                  </td>
                  <td>
                    <Form.Control type="time" value={d.start} onChange={(e) => updateDay(idx, { start: e.target.value })} disabled={!d.enabled} />
                  </td>
                  <td>
                    <Form.Control type="time" value={d.end} onChange={(e) => updateDay(idx, { end: e.target.value })} disabled={!d.enabled} />
                    {rowErr[d.day] && <div className="text-danger small">{rowErr[d.day]}</div>}
                  </td>
                  <td>
                    <Badge bg="light" text="dark">
                      {cfg.timezone}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Exceptions</span>
          <Button size="sm" onClick={() => setExOpen(true)}>
            Add exception
          </Button>
        </Card.Header>
        <Table responsive size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {(cfg.exceptions || []).map((x) => (
              <tr key={x.id}>
                <td>{x.date}</td>
                <td>{x.description}</td>
                <td>{x.type}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title className="h6">Example slots next Monday</Card.Title>
          <div>
            {previewMonday.map((s) => (
              <span key={s} className={styles.slotChip}>
                {s}
              </span>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Modal show={exOpen} onHide={() => setExOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add exception</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Date</Form.Label>
          <DatePicker selected={ex.date} onChange={(d) => d && setEx((s) => ({ ...s, date: d }))} className="form-control mb-2" />
          <Form.Label>Description</Form.Label>
          <Form.Control className="mb-2" value={ex.description} onChange={(e) => setEx((s) => ({ ...s, description: e.target.value }))} />
          <Form.Label>Type</Form.Label>
          <Form.Select value={ex.type} onChange={(e) => setEx((s) => ({ ...s, type: e.target.value }))}>
            <option>Holiday</option>
            <option>Short Day</option>
            <option>Closed</option>
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await addExceptionAPI({
                  date: ex.date.toISOString().slice(0, 10),
                  description: ex.description,
                  type: ex.type,
                });
                toast.success('Added');
                setExOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
